import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { STRINGS } from '@/strings/strings';
import { getBookCheckoutStatus, type BookCheckoutStatusResponse } from '../api';
import { fireBookOrderPaidOnce } from '../book-order-paid-sentinel';
import { useBookShellContext } from '../book-shell-context';
import { PostPaymentWaitScreen } from '../components/PostPaymentWaitScreen';

/**
 * Sebastian ruling 2026-10-08 — `book_order_paid` fires exactly once
 * per order on the success route when the server confirms the
 * payment state (either Stripe's checkout-status = 'completed' OR
 * the fully-covered balance redirect, which only happens after
 * BestellungReviewScreen already saw a `paidWithBalance: true`
 * response from the backend). Order-scoped localStorage dedup so a
 * reload of the success URL does not re-fire.
 *
 * 2026-10-22: fire/sentinel logic lifted to `book-order-paid-sentinel.ts`
 * so `BookRoute.tsx` can share the SAME per-order dedup. A customer
 * reaching `/book/:orderId` in a paid-or-beyond state without ever
 * seeing `/erfolg` (closed the Stripe tab, hard-reloaded, authenticated
 * elsewhere) now fires the event once from the book route. The
 * sentinel key is identical, so no double-count.
 */

/**
 * 6-3 Bildschirm 5 success-flow route —
 * `/book/:orderId/erfolg?session_id={CHECKOUT_SESSION_ID}`.
 *
 * ═══ BLOCK 6 · 6.6 REWRITE (Sebastian ruling 2026-09-09) ═════════════
 *
 * The pre-Block-6 "Vielen Dank" flash screen is REMOVED. The
 * customer sees ONE continuous wait screen from Stripe redirect
 * through the paid-status flip. Implementation:
 *
 *   - While the checkout-status poll is `pending` (webhook not yet
 *     fired) OR `completed`-before-nav, render `PostPaymentWaitScreen`
 *     with the envelope (from the BookShell context). Same visual as
 *     the `/book/:orderId` wait screen for paid/rendering_full/
 *     qa_running — no flash, no context switch.
 *   - On `status === 'completed'`, navigate to `/book/:orderId` where
 *     BookRoute re-dispatches to the same `PostPaymentWaitScreen`
 *     from the envelope status. Continuous.
 *   - `expired` → session timed out (customer waited past 24h on the
 *     Stripe page). Own screen with the "start over" copy.
 *   - Timeout (60s of polling without completed) → honest end state
 *     "Bezahlung erhalten. Wir bereiten dein Buch vor." with a link
 *     back to `/book/:orderId`. The payment DID succeed (we landed
 *     here from Stripe's success_url); what's slow is our webhook
 *     chain.
 *
 * Polling stops on `completed` / `expired` / timeout; unmount
 * clears the timer.
 */

const POLL_INTERVAL_MS = 2000;
// 60 seconds. Anything past this window is a webhook chain problem,
// not a customer-visible one.
const POLL_TIMEOUT_MS = 60_000;

export function BestellungErfolgRoute() {
  const { orderId } = useParams<{ orderId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const { envelope } = useBookShellContext();

  const [status, setStatus] = useState<BookCheckoutStatusResponse['status'] | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startedAtRef = useRef<number | null>(null);

  // P14 pay-with-balance (Sebastian ruling 2026-09-24 block 5) —
  // fully-covered orders reach this route WITHOUT `?session_id=…`
  // (no Stripe hop happened). BestellungReviewScreen navigates here
  // directly on `paidWithBalance: true`. Skip the checkout-status
  // poll entirely and navigate to `/book/:orderId` where BookRoute
  // reads the envelope and re-dispatches to PostPaymentWaitScreen.
  useEffect(() => {
    if (orderId !== undefined && sessionId === null) {
      // Fully-covered balance path (Sebastian ruling 2026-09-24
      // block 5): no Stripe hop, no `salesUnit` to pass. Emits with
      // sales_unit: null so the funnel still counts the paid order;
      // dashboards treating sales_unit as a split dimension get a
      // single `null` bucket for balance-paid orders — small
      // volume today, documented gap.
      fireBookOrderPaidOnce(orderId, null);
      navigate(`/book/${orderId}`, { replace: true });
    }
  }, [orderId, sessionId, navigate]);

  useEffect(() => {
    if (orderId === undefined || sessionId === null) {
      return;
    }

    let cancelled = false;
    startedAtRef.current = Date.now();

    const poll = async () => {
      const startedAt = startedAtRef.current;
      if (startedAt !== null && Date.now() - startedAt >= POLL_TIMEOUT_MS) {
        if (!cancelled) setTimedOut(true);
        return;
      }
      try {
        const result = await getBookCheckoutStatus(orderId);
        if (cancelled) return;
        setStatus(result.status);
        if (result.status === 'completed') {
          // Server-confirmed Stripe payment path. salesUnit is carried
          // on the checkout-status response; book_order_paid fires once
          // per orderId before the redirect. GA4 `purchase` /
          // `book_redeemed` now land SERVER-SIDE via the Measurement
          // Protocol listener on BookPaidEvent (Sebastian ruling
          // 2026-10-21). The client-side purchase emit that used to
          // fire here is retired — single source of truth.
          fireBookOrderPaidOnce(orderId, result.salesUnit);
          navigate(`/book/${orderId}`, { replace: true });
          return;
        }
        if (result.status === 'pending') {
          pollingRef.current = setTimeout(poll, POLL_INTERVAL_MS);
        }
      } catch {
        if (cancelled) return;
        pollingRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    void poll();

    return () => {
      cancelled = true;
      if (pollingRef.current !== null) {
        clearTimeout(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [orderId, sessionId, navigate]);

  if (orderId === undefined || sessionId === null) {
    return null;
  }

  if (timedOut) {
    // Honest end state (Sebastian ruling 2026-08-15). The payment
    // did NOT fail — we landed here from Stripe's success_url. What
    // failed is our webhook chain (Stripe lag / tunnel down / handler
    // errored). Copy is calm; the button navigates to /book/:orderId.
    return (
      <div className="space-y-4 py-8 text-center">
        <h1 className="text-h3 text-dream-navy">{STRINGS.book.bestellung.erfolgTimeoutHeading}</h1>
        <p className="text-base text-dream-navy/70">{STRINGS.book.bestellung.erfolgTimeoutBody}</p>
        <button
          type="button"
          onClick={() => navigate(`/book/${orderId}`, { replace: true })}
          className="h-12 rounded-lg bg-dream-gold px-8 text-base font-bold text-dream-navy hover:bg-dream-gold/90"
        >
          {STRINGS.book.bestellung.erfolgTimeoutCta}
        </button>
      </div>
    );
  }

  if (status === 'expired') {
    return (
      <div className="space-y-4 py-8 text-center">
        <h1 className="text-h3 text-dream-navy">{STRINGS.book.bestellung.erfolgExpiredHeading}</h1>
        <p className="text-base text-dream-navy/70">{STRINGS.book.bestellung.erfolgExpiredNote}</p>
        <button
          type="button"
          onClick={() => navigate(`/book/${orderId}/bestellung`, { replace: true })}
          className="h-12 rounded-lg bg-dream-gold px-8 text-base font-bold text-dream-navy hover:bg-dream-gold/90"
        >
          {STRINGS.book.bestellung.erfolgExpiredCta}
        </button>
      </div>
    );
  }

  // Block 6 · 6.6 · pending or completed-before-nav — render the
  // SAME PostPaymentWaitScreen that /book/:orderId shows for the
  // post-payment statuses. One continuous wait screen from Stripe
  // return through the status flip. Envelope-less transitional
  // window (BookShell hasn't fetched yet) renders nothing so the
  // customer sees a brief blank flash rather than a bombastic
  // "Vielen Dank" intermediate.
  if (envelope === null) return null;
  return <PostPaymentWaitScreen envelope={envelope} />;
}
